import {
    createElement,
    lazy,
    type ComponentType,
    type FunctionComponent,
} from "react";

type Loader = () => Promise<{ default: ComponentType }>;

/** A route component that can also preload its chunk. Typed as a plain
 *  FunctionComponent (not `ComponentType`, whose union confuses JSX) so it can
 *  be used directly as `<Component />`. */
export type PreloadableComponent = FunctionComponent & {
    /** Load the chunk. Once resolved, the component renders synchronously (no
     *  Suspense) — which is what makes it safe for the prerender's
     *  `renderToString` and for hydrating without a mismatch. */
    preload: () => Promise<unknown>;
};

/**
 * Code-split a route while staying compatible with our build-time prerender
 * (React 18 `renderToString`, which cannot await Suspense) and client hydration.
 *
 * Once `preload()` has resolved, the wrapper renders the real module
 * synchronously. So:
 *  - Prerender preloads every route → `renderToString` bakes real HTML.
 *  - The client preloads the current route before `hydrateRoot` → the first
 *    client render matches the prerendered HTML (no hydration mismatch).
 *  - Anything not preloaded falls back to `React.lazy` (Suspense) — e.g. a route
 *    reached via in-app navigation loads its chunk on demand.
 */
export function lazyRoute(loader: Loader): PreloadableComponent {
    let loaded: ComponentType | null = null;
    let inflight: Promise<unknown> | null = null;

    const preload = () => {
        if (loaded) return Promise.resolve();
        if (!inflight) {
            inflight = loader().then((m) => {
                loaded = m.default;
                return m;
            });
        }
        return inflight;
    };

    const LazyComponent = lazy(loader);

    const Wrapper: FunctionComponent = (props) =>
        createElement(loaded ?? LazyComponent, props);

    return Object.assign(Wrapper, { preload });
}
